import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Tracks how long the current video has been allowed to play.
 *
 * The hook starts and pauses timers when playback starts or stops, shows a countdown
 * warning before the time limit is reached, blocks playback when the limit expires,
 * and resets the timers when the user chooses to keep watching.
 */
const useWatchTimeLimit = ({ enabled = false, limitMs = 0, warningMs = 0 }) => {
  const blockTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const warningTickerRef = useRef(null);
  const blockedRef = useRef(false);
  const startedAtRef = useRef(null);
  const elapsedMsRef = useRef(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [warningRemainingMs, setWarningRemainingMs] = useState(0);

  const clearBlockTimer = useCallback(() => {
    if (blockTimerRef.current) {
      clearTimeout(blockTimerRef.current);
      blockTimerRef.current = null;
    }
  }, []);

  const clearWarningTimer = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const clearWarningTicker = useCallback(() => {
    if (warningTickerRef.current) {
      clearInterval(warningTickerRef.current);
      warningTickerRef.current = null;
    }
  }, []);

  const clearTimers = useCallback(() => {
    clearBlockTimer();
    clearWarningTimer();
    clearWarningTicker();
  }, [clearBlockTimer, clearWarningTicker, clearWarningTimer]);

  const getRemainingMs = useCallback(() => {
    const inFlightMs = startedAtRef.current === null ? 0 : Date.now() - startedAtRef.current;
    return Math.max(0, limitMs - elapsedMsRef.current - inFlightMs);
  }, [limitMs]);

  const getIsBlocked = useCallback(() => blockedRef.current, []);

  const startWarningTicker = useCallback(() => {
    if (!enabled || blockedRef.current || warningMs <= 0) return;

    const syncWarningState = () => {
      const remainingMs = getRemainingMs();
      if (remainingMs <= 0 || remainingMs > warningMs) {
        setWarningRemainingMs(0);
        return false;
      }

      setWarningRemainingMs(remainingMs);
      return true;
    };

    clearWarningTicker();

    if (!syncWarningState()) return;

    warningTickerRef.current = setInterval(() => {
      if (!syncWarningState()) {
        clearWarningTicker();
      }
    }, 250);
  }, [clearWarningTicker, enabled, getRemainingMs, warningMs]);

  const scheduleWarning = useCallback(
    (remainingMs) => {
      clearWarningTimer();
      clearWarningTicker();
      setWarningRemainingMs(0);

      if (warningMs <= 0 || remainingMs <= 0) return;

      if (remainingMs <= warningMs) {
        startWarningTicker();
        return;
      }

      warningTimerRef.current = setTimeout(() => {
        warningTimerRef.current = null;
        startWarningTicker();
      }, remainingMs - warningMs);
    },
    [clearWarningTicker, clearWarningTimer, startWarningTicker, warningMs],
  );

  const pause = useCallback(() => {
    clearTimers();
    setWarningRemainingMs(0);

    if (startedAtRef.current === null) return;

    elapsedMsRef.current += Date.now() - startedAtRef.current;
    startedAtRef.current = null;
  }, [clearTimers]);

  const block = useCallback(() => {
    clearTimers();
    blockedRef.current = true;
    startedAtRef.current = null;
    elapsedMsRef.current = limitMs;
    setWarningRemainingMs(0);
    setIsBlocked(true);
  }, [clearTimers, limitMs]);

  const start = useCallback(() => {
    if (!enabled || blockedRef.current || startedAtRef.current !== null) return;

    const remainingMs = limitMs - elapsedMsRef.current;
    if (remainingMs <= 0) {
      block();
      return;
    }

    clearTimers();
    startedAtRef.current = Date.now();
    scheduleWarning(remainingMs);
    blockTimerRef.current = setTimeout(() => {
      blockTimerRef.current = null;
      block();
    }, remainingMs);
  }, [block, clearTimers, enabled, limitMs, scheduleWarning]);

  const reset = useCallback(() => {
    clearTimers();
    blockedRef.current = false;
    startedAtRef.current = null;
    elapsedMsRef.current = 0;
    setWarningRemainingMs(0);
    setIsBlocked(false);
  }, [clearTimers]);

  const accept = useCallback(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (enabled && limitMs > 0) return;

    clearTimers();
    blockedRef.current = false;
    startedAtRef.current = null;
    elapsedMsRef.current = 0;
    setWarningRemainingMs(0);
    setIsBlocked(false);
  }, [clearTimers, enabled, limitMs]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  return useMemo(
    () => ({
      accept,
      getIsBlocked,
      isBlocked,
      isWarningActive: warningRemainingMs > 0,
      pause,
      reset,
      start,
      warningRemainingMs,
      warningSecondsRemaining: warningRemainingMs > 0 ? Math.ceil(warningRemainingMs / 1000) : 0,
    }),
    [accept, getIsBlocked, isBlocked, pause, reset, start, warningRemainingMs],
  );
};

export default useWatchTimeLimit;
